import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase } from '../lib/typeHelper'

const URL = '/api/v1/insure/internal/corgi-portal-session/'

/** Mirrors VendorSession.State; UNKNOWN also stands for "never connected". */
export enum CorgiPortalSessionState {
  Valid = 'VALID',
  Unknown = 'UNKNOWN',
  NeedsHuman = 'NEEDS_HUMAN',
}

/** What a check against the portal found; mirrors the backend's CheckOutcome. */
export enum CorgiPortalCheckOutcome {
  Refreshed = 'refreshed',
  NoSession = 'no_session',
  Rejected = 'rejected',
  Unavailable = 'unavailable',
  Locked = 'locked',
}

export interface CorgiPortalSession {
  state: CorgiPortalSessionState
  accountEmail: string | null
  accountName: string | null
  accessExpiresAt: string | null
  lastRefreshedAt: string | null
  lastRefreshedBy: string | null
}

export interface CorgiPortalCheck extends CorgiPortalSession {
  outcome: CorgiPortalCheckOutcome
}

function getSession(): Promise<CorgiPortalSession> {
  return axios.get(URL).then((response) => toCamelCase(response.data) as CorgiPortalSession)
}

function connect(handoff: string): Promise<CorgiPortalSession> {
  return axios.post(URL, { handoff }).then((response) => toCamelCase(response.data) as CorgiPortalSession)
}

function check(): Promise<CorgiPortalCheck> {
  return axios.post(`${URL}check/`).then((response) => toCamelCase(response.data) as CorgiPortalCheck)
}

export function useCorgiPortalSession() {
  return useQuery({ queryKey: [CacheKeys.CORGI_PORTAL_SESSION], queryFn: getSession })
}

export function useConnectCorgiPortal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: connect,
    onSuccess: (session) => queryClient.setQueryData([CacheKeys.CORGI_PORTAL_SESSION], session),
  })
}

export function useCheckCorgiPortal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: check,
    onSuccess: ({ outcome: _outcome, ...session }) =>
      queryClient.setQueryData([CacheKeys.CORGI_PORTAL_SESSION], session),
  })
}
